/**
 * Hotels-Only Login Page
 * 
 * Direct login to Hotels dashboard - NO industry selector.
 * Clean, single-vertical entry point for hotelsontap.com
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import LanguageSelector from '../../components/LanguageSelector';
import { toast } from 'sonner';
import { Building2, Loader2, Eye, EyeOff } from 'lucide-react';

export default function HotelsLogin() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, setEntryPortal } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(i18n.language === 'fr' ? 'Veuillez entrer courriel et mot de passe' : 'Please enter email and password');
      return;
    }

    setIsLoading(true);
    try {
      // Set Hotels as the entry portal
      setEntryPortal('hotel');
      localStorage.setItem('yul_industry_portal', 'hotel');
      
      await login(email, password, rememberMe);
      toast.success(i18n.language === 'fr' ? 'Bienvenue!' : 'Welcome back!');
      
      // Always go to Hotels dashboard from Hotels login
      navigate('/hotels');
    } catch (error) {
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' ? 'Échec de connexion' : 'Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)] flex flex-col">
      {/* Header with language selector */}
      <header className="p-4 flex justify-end">
        <LanguageSelector variant="compact" />
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">HOTELS ON TAP</CardTitle>
            <CardDescription className="text-[hsl(215,20%,65%)]">
              {i18n.language === 'fr' 
                ? 'Connectez-vous pour gérer vos propriétés'
                : 'Sign in to manage your properties'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">
                  {i18n.language === 'fr' ? 'Courriel' : 'Email'}
                </Label>
                <Input
                  id="email"
                  data-testid="hotels-login-email"
                  type="email"
                  placeholder="admin@hotelsontap.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">
                  {i18n.language === 'fr' ? 'Mot de passe' : 'Password'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="hotels-login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white pr-10"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,20%,65%)]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Remember Me */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  data-testid="hotels-remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-[hsl(217,19%,27%)] bg-[hsl(217,19%,17%)] text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                />
                <Label 
                  htmlFor="rememberMe" 
                  className="text-sm text-[hsl(215,20%,65%)] cursor-pointer"
                >
                  {i18n.language === 'fr' ? 'Se souvenir de moi' : 'Remember me'}
                </Label>
              </div>
              
              <Button
                type="submit"
                data-testid="hotels-login-submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {i18n.language === 'fr' ? 'Connexion...' : 'Signing in...'}
                  </>
                ) : (
                  i18n.language === 'fr' ? 'Se connecter' : 'Sign In'
                )}
              </Button>

              {/* Forgot password hint */}
              <div className="text-center mt-3">
                <p className="text-xs text-[hsl(215,20%,50%)]">
                  {i18n.language === 'fr' 
                    ? 'Mot de passe oublié ? Contactez votre administrateur.' 
                    : 'Forgot your password? Contact your administrator.'}
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
